/*
 * File: elmoApi.ts
 * Project: rosita
 * -----
 * Last Modified: 24 Feb 2022 11:31:37 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { ApiMultipleResponse } from 'models/ApiModels';
import { Substitution, SubstitutionPayload } from 'models/Substitutions';
import { ReduxState } from 'redux/store';
import { SubstitutionParams } from './hermanTheMopParams';

/* ******************** Base Query ******************** */
const baseUrl = process.env.REACT_APP_HERMAN_THE_MOP_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_HERMAN_THE_MOP;

export type LineItemError = {
  title: string;
  errorMessage: string;
};

export const basilBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);

        headers.set('Content-Type', 'application/json');
      }

      return headers;
    }
  })(args, api, extraOptions);
};

export const hermanTheMopApi = createApi({
  reducerPath: 'hermanTheMopApi',
  baseQuery: basilBaseQuery,
  tagTypes: ['Substitutions', 'Substitution'],
  endpoints: (builder) => ({
    getSubstituteItems: builder.query<ApiMultipleResponse<Substitution>, { businessId: string; params: SubstitutionParams }>({
      query: ({ businessId, params }) => ({
        url: `businesses/${businessId}/substitutions`,
        method: 'GET',
        params,
        responseHandler: (response): Promise<ApiMultipleResponse<Substitution> | string> => (response.status >= 300 ? response.text() : response.json())
      }),
      providesTags: ['Substitutions']
    }),
    createSubstituteItem: builder.mutation<Substitution, { businessId: string; payload: SubstitutionPayload }>({
      query: ({ businessId, payload }) => ({
        url: `businesses/${businessId}/substitutions`,
        method: 'POST',
        body: payload,
        responseHandler: (response): Promise<ApiMultipleResponse<Substitution> | string> => (response.status >= 300 ? response.text() : response.json())
      }),
      invalidatesTags: ['Substitutions']
    }),
    getSubstituteItem: builder.query<Substitution, { businessId: string; substituteItemId: string }>({
      query: ({ businessId, substituteItemId }) => ({
        url: `businesses/${businessId}/substitutions/${substituteItemId}`,
        method: 'GET'
        //       responseHandler: (response): Promise<ApiMultipleResponse<Substitution> | string> => (response.status >= 300 ? response.text() : response.json())
      }),
      providesTags: ['Substitution']
    }),
    updateSubstituteItem: builder.mutation<Substitution, { businessId: string; substituteItemId: string; payload: Omit<SubstitutionPayload, 'SubstituteItemId' | 'itemId'> }>({
      query: ({ businessId, substituteItemId, payload }) => ({
        url: `businesses/${businessId}/substitutions/${substituteItemId}`,
        method: 'PUT',
        body: payload,
        responseHandler: (response): Promise<ApiMultipleResponse<Substitution> | string> => (response.status >= 300 ? response.text() : response.json())
      }),
      invalidatesTags: ['Substitutions', 'Substitution']
    }),
    deleteSubstituteItem: builder.mutation<Substitution, { businessId: string; substituteItemId: string }>({
      query: ({ businessId, substituteItemId }) => ({
        url: `businesses/${businessId}/substitutions/${substituteItemId}`,
        method: 'DELETE',
        responseHandler: (response): Promise<ApiMultipleResponse<Substitution> | string> => (response.status >= 300 ? response.text() : response.json())
      }),
      invalidatesTags: ['Substitutions']
    }),

    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        method: 'GET',
        responseHandler: (response): Promise<ApiMultipleResponse<Substitution> | string> => (response.status >= 300 ? response.text() : response.json())
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        method: 'GET',
        responseHandler: (response): Promise<ApiMultipleResponse<Substitution> | string> => (response.status >= 300 ? response.text() : response.json())
      })
    })
  })
});

export const {
  useGetSubstituteItemsQuery,
  useGetSubstituteItemQuery,
  useCreateSubstituteItemMutation,
  useDeleteSubstituteItemMutation,
  useUpdateSubstituteItemMutation,
  useGetVersionQuery,
  useGetApiNameQuery
} = hermanTheMopApi;
